import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { PiasLayoutComponent } from '../../../../shared/components/pias-layout/pias-layout.component';
import { SelectOptions } from '../../../../core/interfaces/select-options.interface';
import { HttpStatusCode } from 'axios';
import { RecrutmentMangerComponentsService } from '../recruitment-manger-services/recrutment-manger-components.service';
import { GetBranchdetails } from '../recruitment-manger-interfaces/get-branchdetails.interface';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { OnboardingApplication } from '../recruitment-manger-components-models/onboarding-application/onboarding-application.model';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../new-rep-application/confirm-popup/confirm-popup.component';

@Component({
  selector: 'app-create-onboarding-application',
  imports: [
    CommonModule,
    PiasLayoutComponent,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDividerModule
  ],
  templateUrl: './create-onboarding-application.component.html',
  styleUrl: './create-onboarding-application.component.scss'
})
export class CreateOnboardingApplicationComponent implements OnInit {

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private sessionStorageService = inject(SessionStorageService);
  private recruitmentManagerService = inject(RecrutmentMangerComponentsService);
  private snackBarService = inject(SnackbarService);
  private matDialog = inject(MatDialog);
  setSprinner = signal(false);
  disabled = signal(true);
  protected currentUserRole: string = "";

  createOnboardApplicationFg!: FormGroup;
  protected branchdetails!: GetBranchdetails;
  protected designations!: SelectOptions[];
  readonly DESIGNATION = {
    FAR: 'Financial Advisor Representative',
    FAM: 'Financial Advisor Manager',
    INDIVIDUAL: 'Introducer - Individual',
    CORPORATE: 'Introducer - Corporate',
  };
  get designation(): string {
    return this.createOnboardApplicationFg.get('designation')?.value;
  }

  get showFields(): boolean {
    return !!this.designation;
  }

  get isAgent(): boolean {
    return (
      this.designation === this.DESIGNATION.FAR ||
      this.designation === this.DESIGNATION.FAM
    );
  }

  get isIndividual(): boolean {
    return this.designation === this.DESIGNATION.INDIVIDUAL;
  }

  get isCorporate(): boolean {
    return this.designation === this.DESIGNATION.CORPORATE;
  }
  get showName(): boolean {
    return this.showFields && !this.isCorporate;
  }

  get showPreferredName(): boolean {
    return !this.isCorporate;
  }

  get showBranch(): boolean {
    return !this.isCorporate;
  }

  get showDirector(): boolean {
    return !this.isCorporate;
  }

  get showManager(): boolean {
    return !this.isCorporate;
  }

  get showReferral(): boolean {
    return this.showFields && this.isAgent;
  }

  get showCompany(): boolean {
    return this.showFields && this.isCorporate;
  }

  get showEmail(): boolean {
    return this.showFields;
  }

  get emailLabel(): string {
    return this.isIndividual || this.isCorporate ? 'Email Address' : 'Personal EmailAddress';
  }

  get showRemarks(): boolean {
    return this.showFields;
  }

  ngOnInit(): void {
    this.createOnboardApplicationFg = this.formBuilder.group({
      name: ['', [Validators.required]],
      preferredName: [''],
      branch: [''],
      financialServicesDirector: [''],
      financialServicesManager: [''],
      designation: ['', [Validators.required]],
      nameofReferral: [''],
      companyName: ['', [Validators.required]],
      personalEmailAddress: ['', [Validators.required, Validators.email]],
      remarksbyRecruitmentManager: [''],

    });

    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();

    if (this.currentUserRole == 'Advisor') {
      this.designations = [
        {
          name: "Introducer - Individual",
          value: "Introducer - Individual"
        },
        {
          name: "Introducer - Corporate",
          value: "Introducer - Corporate"
        }
      ];

    } else {
      this.designations = [
        {
          name: "Financial Advisor Representative",
          value: "Financial Advisor Representative"
        },
        {
          name: "Financial Advisor Manager",
          value: "Financial Advisor Manager"
        },
        {
          name: "Introducer - Individual",
          value: "Introducer - Individual"
        },
        {
          name: "Introducer - Corporate",
          value: "Introducer - Corporate"
        }
      ];

    }

    this.getBranchDetails();

    this.updateValidatorsForDesignation();
    this.createOnboardApplicationFg.get('designation')?.valueChanges.subscribe(() => {
      this.resetConditionalFields();
      this.updateValidatorsForDesignation();
    });

    this.createOnboardApplicationFg.valueChanges.subscribe(() => {
      const identifierValue = this.isCorporate
        ? this.createOnboardApplicationFg.get('companyName')?.value
        : this.createOnboardApplicationFg.get('name')?.value;
      this.disabled.set(!identifierValue);
    });

  }

  private updateValidatorsForDesignation(): void {
    const nameControl = this.createOnboardApplicationFg.get('name');
    const companyNameControl = this.createOnboardApplicationFg.get('companyName');

    if (this.isCorporate) {
      nameControl?.clearValidators();
      companyNameControl?.setValidators([Validators.required]);
    } else {
      nameControl?.setValidators([Validators.required]);
      companyNameControl?.clearValidators();
    }

    nameControl?.updateValueAndValidity({ emitEvent: false });
    companyNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private resetConditionalFields(): void {
    const fieldsToReset = [
      'name',
      'preferredName',
      'companyName',
      'nameofReferral',
      'personalEmailAddress',
      'remarksbyRecruitmentManager'
    ];

    fieldsToReset.forEach((controlName) => {
      const control = this.createOnboardApplicationFg.get(controlName);
      control?.setValue('', { emitEvent: false });
      control?.markAsPristine();
      control?.markAsUntouched();
    });
  }

  async getBranchDetails() {
    this.setSprinner.set(true);
    const responseData = await this.recruitmentManagerService
      .getBranchDetails()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {

      this.branchdetails = responseData.data.responsedata[0];
      this.createOnboardApplicationFg.controls['branch'].setValue(this.branchdetails.branch);
      this.createOnboardApplicationFg.controls['financialServicesDirector'].setValue(this.branchdetails.directorName);
      this.createOnboardApplicationFg.controls['financialServicesManager'].setValue(this.branchdetails.mangaerName);

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }


  async saveOnboardApplication() {
    this.setSprinner.set(true);
    let createnewApplication = new OnboardingApplication();

    createnewApplication.setFullNameNric(this.createOnboardApplicationFg.get('name')?.value);
    createnewApplication.setCompanyName(this.createOnboardApplicationFg.get('companyName')?.value);
    createnewApplication.setPreferredName(this.createOnboardApplicationFg.get('preferredName')?.value);
    createnewApplication.setBranch(this.createOnboardApplicationFg.get('branch')?.value);
    createnewApplication.setFinacialServiceDir(this.createOnboardApplicationFg.get('financialServicesDirector')?.value);
    createnewApplication.setFinacialServiceMan(this.createOnboardApplicationFg.get('financialServicesManager')?.value);
    createnewApplication.setDesignation(this.createOnboardApplicationFg.get('designation')?.value);
    createnewApplication.setPersonalEmailAddres(this.createOnboardApplicationFg.get('personalEmailAddress')?.value);
    createnewApplication.setNameOfReferal(this.createOnboardApplicationFg.get('nameofReferral')?.value);
    createnewApplication.setRemarkContent(this.createOnboardApplicationFg.get('remarksbyRecruitmentManager')?.value);
    createnewApplication.setUpdatedByRole(this.sessionStorageService.getCurrentUserRole());
    createnewApplication.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());

    const responseData = await this.recruitmentManagerService
      .createSaveNewApplication(createnewApplication)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar("Application saved successfully!");
      this.router.navigate(['/agent-onboarding']);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async submitOnboardApplication() {
    this.setSprinner.set(true);
    let createnewApplication = new OnboardingApplication();

    createnewApplication.setFullNameNric(this.createOnboardApplicationFg.get('name')?.value);
    createnewApplication.setCompanyName(this.createOnboardApplicationFg.get('companyName')?.value);
    createnewApplication.setPreferredName(this.createOnboardApplicationFg.get('preferredName')?.value);
    createnewApplication.setBranch(this.createOnboardApplicationFg.get('branch')?.value);
    createnewApplication.setFinacialServiceDir(this.createOnboardApplicationFg.get('financialServicesDirector')?.value);
    createnewApplication.setFinacialServiceMan(this.createOnboardApplicationFg.get('financialServicesManager')?.value);
    createnewApplication.setDesignation(this.createOnboardApplicationFg.get('designation')?.value);
    createnewApplication.setPersonalEmailAddres(this.createOnboardApplicationFg.get('personalEmailAddress')?.value);
    createnewApplication.setNameOfReferal(this.createOnboardApplicationFg.get('nameofReferral')?.value);
    createnewApplication.setRemarkContent(this.createOnboardApplicationFg.get('remarksbyRecruitmentManager')?.value);
    createnewApplication.setUpdatedByRole(this.sessionStorageService.getCurrentUserRole());
    createnewApplication.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());

    const responseData = await this.recruitmentManagerService
      .createSubmitNewApplication(createnewApplication)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar("Application submitted successfully!");
      this.router.navigate(['/agent-onboarding']);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async openConfirmDialog() {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: ' Are you sure you want to submit this Application?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.data === true) {
        // console.log('User confirmed delete');
        // call save API here
        this.submitOnboardApplication();
      } else {
        // console.log('User cancelled');
      }
    });
  }

}
